/**
 * CDB-TQS API — Graph Instances (Neo4j)
 */

import { CDB_TQS_API_BASE_URL, fetchWithLogging } from "./http";

/**
 * ready    — usable. Every instance registered by hand is this.
 * creating — Aura accepted the creation but the database is not reachable yet
 *            (minutes, not seconds). A connection test would fail meanwhile.
 * failed   — provisioning did not complete, or the Aura instance is gone.
 * resizing — a resize asked for in Cortex has not reached its size yet. The
 *            database stays reachable meanwhile (cdb_tqs#1913).
 */
export type GraphProvisioningStatus = "ready" | "creating" | "failed" | "resizing";

/** Size as Aura reports it now (cdb_tqs#1913). */
export interface AuraSize {
  memory: string;
  storage: string | null;
  type: string;
  /** Aura's own status — `updating` also means Aura maintenance, so do not read our resize from it. */
  aura_status: string | null;
}

/** A list-price estimate per month (memory × price per GB-hour × 730 h), never the bill. */
export interface CostEstimate {
  amount: number;
  currency: string;
  price_per_gb_hour: number;
  basis: "list_price";
}

export interface GraphInstanceSizeOption {
  memory: string;
  storage: string;
  estimated_monthly_cost: CostEstimate | null;
}

export interface GraphInstanceSizes {
  instance_type: string;
  region: string;
  cloud_provider: string;
  sizes: GraphInstanceSizeOption[];
}

/**
 * The last stored content count, taken on request ("Neu zählen"), with its
 * time. Aura Professional exposes no store size, so `storage_used_bytes`
 * stays null.
 */
export interface GraphInstanceUsage {
  node_count: number;
  relationship_count: number;
  label_count: number | null;
  storage_used_bytes: number | null;
  measured_at: string;
  sources: Record<string, string>;
}

export interface ResizeGraphInstancePayload {
  memory?: string;
  storage?: string;
}

export interface GraphInstance {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  is_active: boolean;
  is_default: boolean;
  health_status: string;
  /** Null for a self-hosted Neo4j — it has no Aura counterpart to poll or destroy. */
  aura_instance_id?: string | null;
  provisioning_status?: GraphProvisioningStatus;
  resize_target_memory?: string | null;
  resize_target_storage?: string | null;
  /** Null for self-hosted instances, or when Aura could not be asked. */
  aura_size?: AuraSize | null;
  /** Null without a size or without a list price for the type. */
  estimated_monthly_cost?: CostEstimate | null;
  /** Last stored count; null = never counted. */
  usage?: GraphInstanceUsage | null;
  neo4j_uri?: string;
  neo4j_user?: string;
  neo4j_database?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateGraphInstancePayload {
  name: string;
  display_name: string;
  description?: string | null;
  neo4j_uri: string;
  neo4j_user: string;
  neo4j_password: string;
  neo4j_database: string;
  is_active?: boolean;
}

export interface UpdateGraphInstancePayload {
  display_name?: string;
  description?: string | null;
  neo4j_uri?: string;
  neo4j_user?: string;
  neo4j_database?: string;
  is_active?: boolean;
}

/**
 * Provisioning has no credential fields: they do not exist yet. Aura mints them
 * and returns the password exactly once, server-side.
 */
export interface ProvisionGraphInstancePayload {
  name: string;
  display_name: string;
  description?: string | null;
  memory: string;
  neo4j_database?: string;
  is_active?: boolean;
}

export interface TestGraphInstanceConnectionResult {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * A client's graph instance (cdb_tqs#2179). Every client has at most one; its
 * knowledge areas take it, so it cannot change once the client has any.
 * `graph_instance_id` null = not set yet (creating a knowledge area fails).
 */
export interface ClientGraphMapping {
  client_id: string;
  client_name: string;
  graph_instance_id: string | null;
  graph_instance_name: string | null;
  knowledge_area_count: number;
}

export const graphInstancesApi = {
  /**
   * Get all graph instances (Neo4j)
   * @returns List of graph instances
   */
  async getGraphInstances(): Promise<GraphInstance[]> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/graph-instances`,
      { method: "GET" }
    );
    const data = response.data;
    // TQS backend returns { graph_instances: [...], total: number }
    if (data?.graph_instances) return data.graph_instances as GraphInstance[];
    if (Array.isArray(data)) return data as GraphInstance[];
    return [];
  },

  /**
   * Create a new graph instance (Neo4j).
   * No trailing slash — the cdb_tqs route is mounted at /graph-instances
   * (matches the GET pattern above). A trailing slash here triggers a
   * 307 that the BFF redirect handler doesn't survive cleanly (→ 502).
   */
  async createGraphInstance(payload: CreateGraphInstancePayload): Promise<GraphInstance> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/graph-instances`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return response.data as GraphInstance;
  },

  /**
   * Update a graph instance (non-credential fields only — password rotation is a separate call)
   */
  async updateGraphInstance(
    id: string,
    payload: UpdateGraphInstancePayload,
  ): Promise<GraphInstance> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/graph-instances/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    return response.data as GraphInstance;
  },

  /**
   * Rotate the Neo4j password AT the instance (cdb_tqs#1192): the backend
   * decrypts the stored password, changes it via ALTER CURRENT USER, stores
   * the new one and drops the cached driver. The new password is generated
   * server-side and never returned.
   * 409 = the stored password no longer authenticates; record the current
   * one with ``recordGraphInstancePassword`` instead.
   */
  async rotateGraphInstancePassword(id: string): Promise<GraphInstance> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/graph-instances/${id}/rotate-password`,
      { method: "POST", body: JSON.stringify({}) },
    );
    return response.data as GraphInstance;
  },

  /**
   * Record a password that was already changed at the provider (Aura Console,
   * self-hosted). The backend proves it against the instance before storing
   * (422 = rejected, 503 = unreachable; nothing stored either way). Kept
   * separate from updateGraphInstance so a misclick in the edit form cannot
   * overwrite the stored password.
   */
  async recordGraphInstancePassword(id: string, neo4j_password: string): Promise<GraphInstance> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/graph-instances/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ neo4j_password }),
    });
    return response.data as GraphInstance;
  },

  /**
   * Create the Aura database AND register it, in one step.
   * Costs money — the caller must have confirmed that.
   */
  async provisionGraphInstance(
    payload: ProvisionGraphInstancePayload,
  ): Promise<GraphInstance> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/graph-instances/provision`,
      { method: "POST", body: JSON.stringify(payload) },
    );
    return response.data as GraphInstance;
  },

  /**
   * Ask Aura whether a provisioned instance has finished starting.
   * Returns the refreshed row; poll while provisioning_status is "creating".
   */
  async refreshGraphInstanceStatus(id: string): Promise<GraphInstance> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/graph-instances/${id}/refresh-status`,
      { method: "POST", body: JSON.stringify({}) },
    );
    return response.data as GraphInstance;
  },

  /**
   * Sizes Aura offers, each with its estimate. Without an id: what /provision
   * can create. With one: what that instance can be resized to.
   */
  async getGraphInstanceSizes(instanceId?: string): Promise<GraphInstanceSizes> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/graph-instances/sizes`, {
      method: "GET",
      params: instanceId ? { instance_id: instanceId } : undefined,
    });
    return response.data as GraphInstanceSizes;
  },

  /**
   * Resize an Aura instance. Changes recurring cost — the caller must have
   * shown the estimate and had it confirmed. The row is `resizing` until
   * refreshGraphInstanceStatus sees Aura running at the new size (~3 min).
   * 422 = Aura refused the size; its reason is in the error message.
   */
  async resizeGraphInstance(
    id: string,
    payload: ResizeGraphInstancePayload,
  ): Promise<GraphInstance> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/graph-instances/${id}/resize`,
      { method: "POST", body: JSON.stringify(payload) },
    );
    return response.data as GraphInstance;
  },

  /**
   * Count nodes, relationships and labels on the instance and store the
   * result. Returns the instance with its new `usage`. 503 = the instance
   * could not be counted; the stored count is unchanged.
   */
  async recountGraphInstanceUsage(id: string): Promise<GraphInstance> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/graph-instances/${id}/usage`,
      { method: "POST", body: JSON.stringify({}) },
    );
    return response.data as GraphInstance;
  },

  /**
   * Delete a graph instance registration.
   *
   * `deleteAura` additionally destroys the Neo4j database and everything in it.
   * It defaults to false on purpose: re-registering an instance is easy, an
   * erased graph is gone. Only pass true from a dialog that said so plainly.
   */
  async deleteGraphInstance(id: string, deleteAura = false): Promise<void> {
    const query = deleteAura ? "?delete_aura=true" : "";
    await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/graph-instances/${id}${query}`, {
      method: "DELETE",
    });
  },

  /**
   * Promote a graph instance to be the default one for the tenant.
   */
  async setDefaultGraphInstance(id: string): Promise<GraphInstance> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/graph-instances/${id}/set-default`,
      { method: "POST", body: JSON.stringify({}) },
    );
    return response.data as GraphInstance;
  },

  /**
   * Run the connection test against an already-saved graph instance.
   */
  async testGraphInstanceConnectionById(id: string): Promise<TestGraphInstanceConnectionResult> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/graph-instances/${id}/test-connection`,
      { method: "POST", body: JSON.stringify({}) },
    );
    return (response.data ?? { success: false }) as TestGraphInstanceConnectionResult;
  },

  // ---- client → graph instance (cdb_tqs#2179) --------------------------

  /** Every client with its graph instance (null when not set yet). */
  async getClientGraphMappings(): Promise<ClientGraphMapping[]> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/client-graph-mappings`,
      { method: "GET" },
    );
    return (response.data?.mappings ?? []) as ClientGraphMapping[];
  },

  /**
   * Set a client's graph instance. 409 (`code` `client_has_knowledge_areas`)
   * when the client already has knowledge areas and the graph would change.
   */
  async setClientGraphInstance(
    clientId: string,
    graphInstanceId: string,
  ): Promise<ClientGraphMapping> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/client-graph-mappings/${encodeURIComponent(clientId)}`,
      { method: "PUT", body: JSON.stringify({ graph_instance_id: graphInstanceId }) },
    );
    return response.data as ClientGraphMapping;
  },
};
